import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Loader2, LogOut, Trash2, CheckCircle2, CircleDashed, Users } from "lucide-react";

const API = "/api";
const client = axios.create({ withCredentials: true });

const Field = ({ label, value }) =>
  value ? (
    <p className="text-xs text-lilac/80">
      <span className="font-bold uppercase tracking-[0.15em] text-lilac/50">{label}: </span>
      {value}
    </p>
  ) : null;

const Admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    if (window.location.hash?.includes("session_id=")) return;
    client
      .get(`${API}/auth/me`)
      .then(() => setAuth(true))
      .catch(() => setAuth(false));
  }, [location.state]);

  const fetchLeads = () => {
    setLoading(true);
    client
      .get(`${API}/leads`)
      .then((r) => setLeads(r.data))
      .catch(() => toast.error("No se pudieron cargar los inscriptos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (auth) fetchLeads();
  }, [auth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await client.post(`${API}/auth/logout`).catch(() => {});
    setAuth(false);
    navigate("/admin", { replace: true });
  };

  const toggleEstado = async (lead) => {
    const nuevo = lead.estado === "pagado" ? "pendiente" : "pagado";
    try {
      await client.patch(`${API}/leads/${lead.id}`, { estado: nuevo });
      setLeads(leads.map((l) => (l.id === lead.id ? { ...l, estado: nuevo } : l)));
      toast.success(`${lead.nombre} marcado como ${nuevo}`);
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const eliminar = async (lead) => {
    if (!window.confirm(`¿Eliminar la inscripción de ${lead.nombre} ${lead.apellido || ""}?`)) return;
    try {
      await client.delete(`${API}/leads/${lead.id}`);
      setLeads(leads.filter((l) => l.id !== lead.id));
      toast.success("Inscripción eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-plum" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-magenta" />
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-plum px-6" data-testid="admin-login">
        <div className="glass w-full max-w-sm p-10 text-center">
          <img src="/assets/logo-meli.webp" alt="Meli Market Minds" className="mx-auto h-10 w-auto mix-blend-screen" />
          <h1 className="mt-8 font-display text-xl font-extrabold uppercase tracking-tight text-rosewhite">
            Panel de inscriptos
          </h1>
          <p className="mt-2 text-xs text-lilac/70">Acceso privado. Solo la cuenta autorizada.</p>
          {location.state?.authError && (
            <p
              className="mt-4 border border-red-400/50 bg-red-400/10 px-4 py-3 text-xs leading-relaxed text-red-300"
              data-testid="admin-login-error"
            >
              {location.state.authError}
            </p>
          )}
          <button
            onClick={login}
            data-testid="admin-login-google"
            className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-magenta px-6 py-3.5 font-display text-xs font-extrabold uppercase tracking-[0.2em] text-plum transition-colors duration-300 hover:bg-neoncyan"
          >
            Entrar con Google
          </button>
          <a href="/" className="mt-6 block text-xs uppercase tracking-[0.2em] text-lilac/50 hover:text-magenta">
            ← Volver a la página
          </a>
        </div>
      </div>
    );
  }

  const pagados = leads.filter((l) => l.estado === "pagado").length;

  return (
    <div className="min-h-screen bg-plum px-6 py-10 text-rosewhite md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-meli.webp" alt="Meli Market Minds" className="h-9 w-auto mix-blend-screen" />
            <h1 className="font-display text-lg font-extrabold uppercase tracking-tight">Inscriptos al curso</h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-lilac/70 transition-colors hover:border-magenta hover:text-magenta"
          >
            <LogOut className="h-3.5 w-3.5" /> Salir
          </button>
        </header>

        <div className="mt-8 flex flex-wrap gap-3" data-testid="admin-stats">
          <span className="glass flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em]">
            <Users className="h-3.5 w-3.5 text-neoncyan" /> Total: {leads.length}
          </span>
          <span className="glass flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em]">
            <CheckCircle2 className="h-3.5 w-3.5 text-neoncyan" /> Pagados: {pagados}
          </span>
          <span className="glass flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em]">
            <CircleDashed className="h-3.5 w-3.5 text-magenta" /> Pendientes: {leads.length - pagados}
          </span>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-magenta" />
          </div>
        ) : leads.length === 0 ? (
          <p className="mt-16 text-center font-editorial text-2xl italic text-lilac/70" data-testid="admin-empty">
            Todavía no hay inscriptos.
          </p>
        ) : (
          <div className="mt-8 flex flex-col gap-4">
            {leads.map((lead) => (
              <article key={lead.id} className="glass p-6" data-testid={`admin-lead-${lead.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-base font-extrabold uppercase tracking-tight">
                      {lead.nombre} {lead.apellido}
                    </p>
                    <p className="mt-1 text-xs text-lilac/70">
                      {lead.email} · {lead.whatsapp} · {lead.metodo_pago === "paypal" ? "PayPal USD 100" : "QR ₲600.000"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${
                        lead.estado === "pagado"
                          ? "bg-neoncyan/15 text-neoncyan"
                          : "bg-magenta/15 text-magenta"
                      }`}
                      data-testid={`lead-estado-${lead.id}`}
                    >
                      {lead.estado}
                    </span>
                    <button
                      onClick={() => toggleEstado(lead)}
                      data-testid={`lead-toggle-${lead.id}`}
                      className="border border-neoncyan/50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-neoncyan transition-colors hover:bg-neoncyan hover:text-deepteal"
                    >
                      {lead.estado === "pagado" ? "Marcar pendiente" : "Marcar pagado"}
                    </button>
                    <button
                      onClick={() => eliminar(lead)}
                      data-testid={`lead-delete-${lead.id}`}
                      className="border border-red-400/50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-red-300 transition-colors hover:bg-red-400 hover:text-plum"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid gap-1.5 border-t border-white/10 pt-4 md:grid-cols-2">
                  <Field label="Perfil" value={lead.perfil === "Otro" ? `Otro: ${lead.perfil_otro}` : lead.perfil} />
                  <Field label="Masterclass" value={lead.masterclass} />
                  <Field label="Se enteró por" value={lead.fuente === "Otro" ? `Otro: ${lead.fuente_otro}` : lead.fuente} />
                  <Field
                    label="Fecha"
                    value={lead.timestamp ? new Date(lead.timestamp).toLocaleString("es-PY") : ""}
                  />
                  <Field label="Sobre ella/él" value={lead.sobre_vos} />
                  <Field label="Quiere aprender" value={lead.interes} />
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

export default Admin;
